// RolAdmin.js
import React, { useState, useCallback, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "bootstrap-icons/font/bootstrap-icons.css";

const RolAdmin = ({ isOpen, toggleSidebar, closeSidebar }) => {
  const [openMenus, setOpenMenus] = useState({});
  const [fadeMenus, setFadeMenus] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Función para alternar un submenú
  const toggleMenu = useCallback(
    (menu) => {
      if (!isOpen) {
        toggleSidebar();
        setOpenMenus((prev) => ({ ...prev, [menu]: true }));
        setTimeout(() => {
          setFadeMenus((prev) => ({ ...prev, [menu]: true }));
        }, 10);
      } else {
        const newState = !openMenus[menu];
        setOpenMenus((prev) => ({ ...prev, [menu]: newState }));
        if (newState) {
          setTimeout(() => {
            setFadeMenus((prev) => ({ ...prev, [menu]: true }));
          }, 10);
        } else {
          setFadeMenus((prev) => ({ ...prev, [menu]: false }));
        }
      }
    },
    [isOpen, toggleSidebar, openMenus],
  );

  useEffect(() => {
    // construye un objeto con las claves de tus menús
    const newOpen = {
      Clientes:
        location.pathname.startsWith("/facturas-de-clientes") ||
        location.pathname.startsWith("/cotizacion") ||
        location.pathname.startsWith("/remisiones") ||
        location.pathname.startsWith("/pedidos") ||
        location.pathname.startsWith("/pagos-recibidos") ||
        location.pathname.startsWith("/calculador"),
      Proveedores:
        location.pathname.startsWith("/facturas-de-proveedores") ||
        location.pathname.startsWith("/pagos") ||
        location.pathname.startsWith("/notas-de-debito") ||
        location.pathname === "/orden" ||
        location.pathname.startsWith("/orden/") ||
        location.pathname.startsWith("/requisiciones"),
      Inventario:
        location.pathname.startsWith("/articulos-venta") ||
        location.pathname.startsWith("/articulos-compra") ||
        location.pathname.startsWith("/ajustes-de-inventario") ||
        location.pathname.startsWith("/gestion-de-productos") ||
        location.pathname.startsWith("/categorias") ||
        location.pathname.startsWith("/almacenes") ||
        location.pathname.startsWith("/grupos"),
      Reportes:
        location.pathname.startsWith("/generador") ||
        location.pathname.startsWith("/opcion-2"),
      Contactos:
        location.pathname.startsWith("/todos") ||
        location.pathname.startsWith("/clientes") ||
        location.pathname.startsWith("/proveedores"),
      Formatos:
        location.pathname.startsWith("/carta-envio") ||
        location.pathname.startsWith("/carta-ingresos") ||
        location.pathname.startsWith("/carta-nueva") ||
        location.pathname.startsWith("/carta-poder"),
      Configuracion:
        location.pathname.startsWith("/usuarios") ||
        location.pathname.startsWith("/personalizacion"),
      Sucursales:
        location.pathname.startsWith("/sucursales"),
    };
    setOpenMenus(newOpen);
  }, [location.pathname]);

  // Cierra todos los submenús
  const resetMenus = useCallback(() => {
    setOpenMenus({}); // Resetea todos los menús abiertos
    closeSidebar(); // Cierra el sidebar
  }, [closeSidebar]);

  const isMenuOpen = (menu) => openMenus[menu] || false;

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem("userRole"); // Eliminar el rol de localStorage al cerrar sesión
    navigate("/login");
  };

  // Función personalizada para manejar el clic en los enlaces
  const handleLinkClick = (e, path) => {
    if (location.pathname !== path) {
      navigate(path); // agrega esto si quieres navegación explícita
      closeSidebar(); // solo ciérralo si realmente navegas
    }
    // No hagas nada si estás ya en esa ruta
  };

  // Función personalizada para manejar el clic en los iconos de plus
  const handlePlusIconClick = (e, path) => {
    e.preventDefault(); // Evita la navegación predeterminada del enlace
    e.stopPropagation(); // Previene que el evento burbujee al <Link>
    navigate(path, { state: { openModal: true } });
    closeSidebar(); // Cierra el Sidebar
  };

  return (
    <div className={`sidebar-container ${isOpen ? "expanded" : "collapsed"}`}>
      <div className="sidebar-header">
        <div className="logo-container-sidebar">
          <Link to="/" onClick={resetMenus}>
            {isOpen ? (
              // Logo grande cuando está expandido
              <img
                className="sidebar-logo logo-expanded"
                src="/img/logo-comercial.svg"
                alt="ICIAMEX logo"
              />
            ) : (
              // Ícono pequeño (ic.svg) cuando está colapsado
              <img
                className="sidebar-logo logo-small"
                src="/img/ic.svg"
                alt="Menu Icon"
              />
            )}
          </Link>
        </div>

        {/* Aquí va el “cuadro” que contiene el ícono de abrir/cerrar */}
        {isOpen ? (
          <div className="toggle-box" onClick={resetMenus}>
            <i className="bi bi-chevron-left"></i>
          </div>
        ) : (
          <div className="toggle-box" onClick={toggleSidebar}>
            <i className="bi bi-chevron-right"></i>
          </div>
        )}
      </div>

      <li className="separator separator-logo" aria-hidden="true"></li>

      <div className="sidebar-content">
        <nav id="primary-menu" className="navbar">
          <ul className="nav navbar-nav">
            {isOpen && <li className="menu-section">Principal</li>}

            <li
              className={`menu-item ${
                location.pathname === "/" ? "active" : ""
              }`}
              data-tooltip="Inicio"
              onClick={() => handleLinkClick("/", "/")}
            >
              <Link to="/">
                <i className="bi bi-house"></i>
                {isOpen && <span className="item-text">Inicio</span>}
              </Link>
            </li>

            <li
              className={`menu-item ${
                location.pathname === "/tareas" ? "active" : ""
              }`}
              data-tooltip="Tareas"
              onClick={(e) => handleLinkClick(e, "/tareas")}
            >
              <Link to="/tareas">
                <i className="bi bi-check2-square"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Tareas</span>
                  </>
                )}
              </Link>
            </li>

            <li
              className={`menu-item ${
                location.pathname === "/calendario" ? "active" : ""
              }`}
              data-tooltip="Calendario"
              onClick={(e) => handleLinkClick(e, "/calendario")}
            >
              <Link to="/calendario">
                <i className="bi bi-calendar2-day"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Calendario</span>
                  </>
                )}
              </Link>
            </li>

            <li className="separator" aria-hidden="true"></li>

            {isOpen && <li className="menu-section">Utilidades</li>}

            <li
              className={[
                "menu-item",
                "submenu-parent",
                isMenuOpen("Clientes") ? "open" : "",
                location.pathname === "/facturas-de-clientes" ||
                location.pathname.startsWith("/cotizacion") ||
                location.pathname.startsWith("/remisiones") ||
                location.pathname.startsWith("/pedidos") ||
                location.pathname === "/pagos-recibidos" ||
                location.pathname === "/calculador"
                  ? "active"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-tooltip="Clientes"
              onClick={() => toggleMenu("Clientes")}
            >
              <div>
                <i className="bi bi-graph-up-arrow"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Clientes</span>
                    <i
                      className={`bi ${
                        isMenuOpen("Clientes")
                          ? "bi-chevron-up"
                          : "bi-chevron-down"
                      } arrow`}
                    />
                  </>
                )}
              </div>
            </li>

            {isMenuOpen("Clientes") && (
              <ul
                className={`submenu ${
                  fadeMenus.Clientes ? "submenu-fade" : ""
                } ${isMenuOpen("Clientes") ? "open" : "closed"}`}
              >
                {/* —————————————————— Cotizaciones (+) —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/cotizacion"
                    className={`submenu-link ${
                      location.pathname === "/cotizacion" ? "active" : ""
                    }`}
                    onClick={(e) => handleLinkClick(e, "/cotizacion")}
                  >
                    <span className="submenu-label">Cotizaciones</span>
                  </Link>

                  <i
                    className="bi bi-plus-circle plus-icon"
                    onClick={(e) => handlePlusIconClick(e, "/cotizacion")}
                  />
                </li>

                {/* —————————————————— Pedidos (+) —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/pedidos"
                    className={`submenu-link ${
                      location.pathname === "/pedidos" ? "active" : ""
                    }`}
                    onClick={(e) => handleLinkClick(e, "/pedidos")}
                  >
                    <span className="submenu-label">Pedidos</span>
                  </Link>

                  <i
                    className="bi bi-plus-circle plus-icon"
                    onClick={(e) => handlePlusIconClick(e, "/pedidos")}
                  />
                </li>

                {/* —————————————————— Remisiones (+) —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/remisiones"
                    className={`submenu-link ${
                      location.pathname === "/remisiones" ? "active" : ""
                    }`}
                    onClick={(e) => handleLinkClick(e, "/remisiones")}
                  >
                    <span className="submenu-label">Remisiones</span>
                  </Link>

                  <i
                    className="bi bi-plus-circle plus-icon"
                    onClick={(e) => handlePlusIconClick(e, "/remisiones")}
                  />
                </li>

                {/* —————————————————— Facturas —————————————————— */}
                <li
                  className="submenu-item"
                  onClick={(e) => handleLinkClick(e, "/facturas-de-clientes")}
                >
                  <Link to="/facturas-de-clientes" className="submenu-link">
                    <span className="submenu-label">Facturas de Clientes</span>
                  </Link>
                </li>
              </ul>
            )}

            <li
              className={[
                "menu-item",
                "submenu-parent",
                isMenuOpen("Proveedores") ? "open" : "",
                location.pathname === "/facturas-de-proveedores" ||
                location.pathname === "/pagos" ||
                location.pathname === "/notas-de-debito" ||
                location.pathname === "/orden" ||
                location.pathname.startsWith("/orden/") ||
                location.pathname.startsWith("/requisiciones")
                  ? "active"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-tooltip="Proveedores"
              onClick={() => toggleMenu("Proveedores")}
            >
              <div>
                <i className="bi bi-currency-dollar"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Proveedores</span>
                    <i
                      className={`bi ${
                        isMenuOpen("Proveedores")
                          ? "bi-chevron-up"
                          : "bi-chevron-down"
                      } arrow`}
                    />
                  </>
                )}
              </div>
            </li>

            {isMenuOpen("Proveedores") && (
              <ul
                className={`submenu ${
                  fadeMenus.Proveedores ? "submenu-fade" : ""
                } ${isMenuOpen("Proveedores") ? "open" : "closed"}`}
              >
                {/* —————————————————— Requisiciones —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/requisiciones"
                    className={`submenu-link ${
                      location.pathname === "/requisiciones" ? "active" : ""
                    }`}
                    onClick={(e) => handleLinkClick(e, "/requisiciones")}
                  >
                    <span className="submenu-label">
                      Requisiciones de Compra
                    </span>
                  </Link>
                </li>

                {/* —————————————————— Órdenes de Compra (+) —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/orden"
                    className={`submenu-link ${
                      location.pathname === "/orden" ? "active" : ""
                    }`}
                    onClick={(e) => handleLinkClick(e, "/orden")}
                  >
                    <span className="submenu-label">Órdenes de Compra</span>
                  </Link>

                  <i
                    className="bi bi-plus-circle plus-icon"
                    onClick={(e) => handlePlusIconClick(e, "/orden")}
                  />
                </li>

                {/* —————————————————— Pagos —————————————————— */}
                <li className="submenu-item">
                  <Link to="#" className="submenu-link" onClick={() => {}}>
                    <span className="submenu-label">Pagos</span>
                  </Link>
                </li>

                {/* —————————————————— Facturas —————————————————— */}
                <li
                  className="submenu-item"
                  onClick={(e) =>
                    handleLinkClick(e, "/facturas-de-proveedores")
                  }
                >
                  <Link to="/facturas-de-proveedores" className="submenu-link">
                    <span className="submenu-label">
                      Facturas de Proveedores
                    </span>
                  </Link>
                </li>
              </ul>
            )}

            <li
              className={[
                "menu-item",
                "submenu-parent",
                isMenuOpen("Inventario") ? "open" : "",
                location.pathname === "/articulos-venta" ||
                location.pathname === "/articulos-compra" ||
                location.pathname === "/ajustes-de-inventario" ||
                location.pathname === "/gestion-de-productos" ||
                location.pathname === "/categorias" ||
                location.pathname === "/almacenes" ||
                location.pathname === "/grupos"
                  ? "active"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-tooltip="Inventario"
              onClick={() => toggleMenu("Inventario")}
            >
              <div>
                <i className="bi bi-box"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Inventario</span>
                    <i
                      className={`bi ${
                        isMenuOpen("Inventario")
                          ? "bi-chevron-up"
                          : "bi-chevron-down"
                      } arrow`}
                    />
                  </>
                )}
              </div>
            </li>

            {isMenuOpen("Inventario") && (
              <ul
                className={`submenu ${
                  fadeMenus.Inventario ? "submenu-fade" : ""
                } ${isMenuOpen("Inventario") ? "open" : "closed"}`}
              >
                {/* —————————————————— Artículos de Venta —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/articulos-venta"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/articulos-venta")}
                  >
                    <span className="submenu-label">Artículos de Venta</span>
                  </Link>
                </li>

                {/* —————————————————— Artículos de Compra —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/articulos-compra"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/articulos-compra")}
                  >
                    <span className="submenu-label">Artículos de Compra</span>
                  </Link>
                </li>

                {/* —————————————————— Ajustes de Inventario —————————————————— */}
                <li className="submenu-item">
                  <Link to="#" className="submenu-link" onClick={() => {}}>
                    <span className="submenu-label">Ajustes de Inventario</span>
                  </Link>
                </li>

                {/* —————————————————— Gestión de Productos —————————————————— */}
                <li className="submenu-item">
                  <Link to="#" className="submenu-link" onClick={() => {}}>
                    <span className="submenu-label">Gestión de Productos</span>
                  </Link>
                </li>

                {/* —————————————————— Categorías —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/categorias"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/categorias")}
                  >
                    <span className="submenu-label">Categorías</span>
                  </Link>
                </li>

                {/* —————————————————— Almacenes —————————————————— */}
                <li className="submenu-item">
                  <Link to="#" className="submenu-link" onClick={() => {}}>
                    <span className="submenu-label">Almacenes</span>
                  </Link>
                </li>

                {/* —————————————————— Grupos —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/grupos"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/grupos")}
                  >
                    <span className="submenu-label">Grupos</span>
                  </Link>
                </li>
              </ul>
            )}

            {/* —————————————————— Sucursales —————————————————— */}
            <li
              className={`menu-item ${
                location.pathname.startsWith("/sucursales") ? "active" : ""
              }`}
              data-tooltip="Sucursales"
            >
              <Link
                to="/sucursales" // ← sin query; abrirá PERFIL por defecto
                className="submenu-link"
                onClick={(e) => handleLinkClick?.(e, "/sucursales")}
              >
                <i class="bi bi-shop"></i>
                {isOpen && <span className="item-text">Sucursales</span>}
              </Link>
            </li>

            <li
              className={[
                "menu-item",
                "submenu-parent",
                isMenuOpen("Reportes") ? "open" : "",
                location.pathname === "/generador" ||
                location.pathname === "/opcion-2"
                  ? "active"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-tooltip="Reportes"
              onClick={() => toggleMenu("Reportes")}
            >
              <div>
                <i className="bi bi-file-earmark-bar-graph"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Reportes</span>
                    <i
                      className={`bi ${
                        isMenuOpen("Reportes")
                          ? "bi-chevron-up"
                          : "bi-chevron-down"
                      } arrow`}
                    />
                  </>
                )}
              </div>
            </li>

            {isMenuOpen("Reportes") && (
              <ul
                className={`submenu ${
                  fadeMenus.Reportes ? "submenu-fade" : ""
                } ${isMenuOpen("Reportes") ? "open" : "closed"}`}
              >
                {/* —————————————————— Generador de Obra —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/generador"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/generador")}
                  >
                    <span className="submenu-label">Generador de Obra</span>
                  </Link>
                </li>

                {/* —————————————————— Opción 2 —————————————————— */}
                <li className="submenu-item">
                  <Link to="#" className="submenu-link" onClick={() => {}}>
                    <span className="submenu-label">Opción 2</span>
                  </Link>
                </li>
              </ul>
            )}

            <li
              className={[
                "menu-item",
                "submenu-parent",
                isMenuOpen("Contactos") ? "open" : "",
                location.pathname === "/todos" ||
                location.pathname === "/clientes" ||
                location.pathname === "/proveedores"
                  ? "active"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-tooltip="Contactos"
              onClick={() => toggleMenu("Contactos")}
            >
              <div>
                <i className="bi bi-telephone"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Contactos</span>
                    <i
                      className={`bi ${
                        isMenuOpen("Contactos")
                          ? "bi-chevron-up"
                          : "bi-chevron-down"
                      } arrow`}
                    />
                  </>
                )}
              </div>
            </li>

            {isMenuOpen("Contactos") && (
              <ul
                className={`submenu ${
                  fadeMenus.Contactos ? "submenu-fade" : ""
                } ${isMenuOpen("Contactos") ? "open" : "closed"}`}
              >
                {/* —————————————————— Todos —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/todos"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/todos")}
                  >
                    <span className="submenu-label">Todos</span>
                  </Link>
                </li>

                {/* —————————————————— Clientes —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/clientes"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/clientes")}
                  >
                    <span className="submenu-label">Clientes</span>
                  </Link>
                </li>

                {/* —————————————————— Proveedores —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/proveedores"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/proveedores")}
                  >
                    <span className="submenu-label">Proveedores</span>
                  </Link>
                </li>
              </ul>
            )}

            {/* FORMATOS */}

            <li
              className={[
                "menu-item",
                "submenu-parent",
                isMenuOpen("Formatos") ? "open" : "",
                location.pathname === "/carta-ingresos" ||
                location.pathname === "/carta-envio" ||
                location.pathname === "/carta-nueva" ||
                location.pathname === "/carta-poder"
                  ? "active"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-tooltip="Formatos"
              onClick={() => toggleMenu("Formatos")}
            >
              <div>
                <i className="bi bi-file-earmark-post"></i>
                {isOpen && (
                  <>
                    <span className="item-text">Formatos</span>
                    <i
                      className={`bi ${
                        isMenuOpen("Formatos")
                          ? "bi-chevron-up"
                          : "bi-chevron-down"
                      } arrow`}
                    />
                  </>
                )}
              </div>
            </li>

            {isMenuOpen("Formatos") && (
              <ul
                className={`submenu ${
                  fadeMenus.Formatos ? "submenu-fade" : ""
                } ${isMenuOpen("Formatos") ? "open" : "closed"}`}
              >
                {/* —————————————————— Todos —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/carta-ingresos"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/carta-ingresos")}
                  >
                    <span className="submenu-label">Carta Ingresos</span>
                  </Link>
                </li>

                {/* —————————————————— Clientes —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/carta-envio"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/carta-envio")}
                  >
                    <span className="submenu-label">Carta Envió</span>
                  </Link>
                </li>

                {/* —————————————————— Proveedores —————————————————— */}
                <li className="submenu-item">
                  <Link
                    to="/carta-nueva"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/carta-nueva")}
                  >
                    <span className="submenu-label">Carta Nueva</span>
                  </Link>
                </li>

                <li className="submenu-item">
                  <Link
                    to="/carta-poder"
                    className="submenu-link"
                    onClick={(e) => handleLinkClick(e, "/carta-poder")}
                  >
                    <span className="submenu-label">Carta Poder</span>
                  </Link>
                </li>
              </ul>
            )}

            <li className="separator" aria-hidden="true"></li>

            {isOpen && <li className="menu-section">Preferencias</li>}

            <li
              className={`menu-item ${
                location.pathname.startsWith("/configuracion") ? "active" : ""
              }`}
              data-tooltip="Configuración"
            >
              <Link
                to="/configuracion" // ← sin query; abrirá PERFIL por defecto
                className="submenu-link"
                onClick={(e) => handleLinkClick?.(e, "/configuracion")}
              >
                <i className="bi bi-gear"></i>
                {isOpen && <span className="item-text">Configuración</span>}
              </Link>
            </li>
          </ul>
        </nav>

        <div className="profile-header">
          <img
            className="profile-avatar"
            src="/img/plan.svg"
            alt="Avatar Usuario"
          />
          <div className="profile-text">
            <span style={{color: "#fff"}}>Timeweb</span>
            <span className="profile-role">Administrador</span>
          </div>
          <i className="bi bi-chevron-down"></i>
        </div>

        <li className="separator" aria-hidden="true"></li>

        {/* ─── Botón de Cerrar Sesión (footer) ───────────────────────────────────────────────── */}
        {isOpen ? (
          <div className="sidebar-footer">
            <div className="profile-expanded">
              <button className="btn-logout-extended" onClick={handleLogout}>
                <i className="bi bi-box-arrow-in-right"></i>
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        ) : (
          <li
            className="menu-item logout"
            data-tooltip="Cerrar Sesión"
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-in-right"></i>
          </li>
        )}

        <Outlet />
      </div>
    </div>
  );
};

export default RolAdmin;
